import { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  AutoFixHigh as AutoFixHighIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  MoreVert as MoreVertIcon,
  DragIndicator as DragIndicatorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Article as ArticleIcon,
  TableChart as TableChartIcon,
  Slideshow as SlideshowIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Assignment as TaskIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type DocumentFile = {
  id: string;
  name: string;
  filename?: string;
  size: number;
  size_bytes?: number;
  modified: string;
  uploaded_at?: string;
  url?: string;
  folder?: string;
  storage_key?: string;
  type?: 'generated' | 'uploaded' | 'edited';
  status?: 'pending' | 'ready' | 'error';
  is_operative?: boolean;
  is_current?: boolean;
  has_pending_version?: boolean;
  display_order?: number;
  root_document_id?: string;
  category?: {
    id: string;
    name: string;
    color: string;
  } | null;
};

interface CaseFilesProps {
  // Data
  documents: DocumentFile[];
  loading?: boolean;
  caseId?: string;

  // Features to enable/disable
  showGenerate?: boolean;
  showDownloadAll?: boolean;
  showUpload?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showSync?: boolean;
  enableReordering?: boolean;

  // State indicators
  generating?: boolean;
  downloadingAll?: boolean;
  syncing?: boolean;
  lastSynced?: string | null;

  // Event handlers
  onGenerate?: () => void;
  onDownloadAll?: () => void;
  onUpload?: () => void;
  onDownload?: (doc: DocumentFile) => void;
  onCardClick?: (doc: DocumentFile) => void;
  onEdit?: (doc: DocumentFile) => void;
  onDelete?: (doc: DocumentFile) => void;
  onViewVersions?: (doc: DocumentFile) => void;
  onSync?: () => void;
  onDocumentsReordered?: (documents: DocumentFile[]) => void;
  onRename?: (documentId: string, newName: string, categoryId: string | null, customDate: string | null, dateExplanation: string, notes: string) => Promise<void>;
  onApprove?: (doc: DocumentFile) => Promise<void>;

  // Highlighting
  highlightDocumentId?: string;

  // Styling
  title?: string;
  emptyMessage?: string;
  generatingMessage?: string;

  // Search
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

interface SortableDocumentItemProps {
  doc: DocumentFile;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, doc: DocumentFile) => void;
  onCardClick: (doc: DocumentFile) => void;
  isHighlighted: boolean;
}

// Helper function to get the appropriate icon based on file extension
function getFileIcon(filename: string) {
  const ext = filename.toLowerCase().split('.').pop() || '';

  switch (ext) {
    case 'doc':
    case 'docx':
      return <ArticleIcon color="primary" />;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return <TableChartIcon color="primary" />;
    case 'ppt':
    case 'pptx':
      return <SlideshowIcon color="primary" />;
    case 'pdf':
      return <PdfIcon color="primary" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
      return <ImageIcon color="primary" />;
    default:
      return <DocumentIcon color="primary" />;
  }
}

function SortableDocumentItem({
  doc,
  onMenuOpen,
  onCardClick,
  isHighlighted,
}: SortableDocumentItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: doc.id });

  const highlightRef = useRef<HTMLLIElement>(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Scroll to highlighted document
  useEffect(() => {
    if (isHighlighted && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500); // Delay to allow tab switching and rendering to complete
    }
  }, [isHighlighted, doc.id, doc.name]);

  return (
    <ListItem
      ref={(node) => {
        setNodeRef(node);
        if (node) {
          (highlightRef as any).current = node;
        }
      }}
      style={style}
      onClick={() => onCardClick(doc)}
      sx={{
        border: '2px solid',
        borderColor: isHighlighted ? 'primary.main' : doc.has_pending_version ? 'warning.main' : 'divider',
        borderRadius: 1,
        mb: 1,
        backgroundColor: isHighlighted ? 'primary.50' : doc.has_pending_version ? 'warning.50' : 'background.paper',
        cursor: isDragging ? 'grabbing' : 'pointer',
        boxShadow: isHighlighted ? '0 0 0 3px rgba(25, 118, 210, 0.2)' : 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: isHighlighted ? 'primary.100' : doc.has_pending_version ? 'warning.100' : 'grey.50',
        },
      }}
    >
      {/* Drag Handle */}
      <ListItemIcon
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab', minWidth: 36 }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <DragIndicatorIcon color="action" />
      </ListItemIcon>

      <ListItemIcon>
        {getFileIcon(doc.filename || doc.name)}
      </ListItemIcon>

      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {doc.name}
              </Typography>

              {/* Category Badge */}
              {doc.category && (
                <Chip
                  label={doc.category.name}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    flexShrink: 0,
                    backgroundColor: doc.category.color,
                    color: '#fff',
                  }}
                />
              )}
            </Box>

            {/* Spacer to push status badges to the right */}
            <Box sx={{ flex: 1 }} />

            {/* Status Badges */}
            {doc.is_operative ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Operative"
                size="small"
                color="success"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold', flexShrink: 0 }}
              />
            ) : (
              <Chip
                icon={<ScheduleIcon />}
                label="Pending Approval"
                size="small"
                color="default"
                sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0, mr: 2 }}
              />
            )}

            {doc.has_pending_version && (
              <Chip
                label="New Version Pending"
                size="small"
                color="warning"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold', flexShrink: 0 }}
              />
            )}
          </Box>
        }
        secondary={`${(doc.size / 1024).toFixed(1)} KB • Modified: ${new Date(doc.modified).toLocaleString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })}`}
      />

      <IconButton
        size="medium"
        onClick={(e) => {
          e.stopPropagation(); // Prevent card click
          onMenuOpen(e, doc);
        }}
      >
        <MoreVertIcon />
      </IconButton>
    </ListItem>
  );
}

export default function CaseFiles({
  documents,
  loading = false,
  caseId,
  showGenerate = false,
  showDownloadAll = false,
  showUpload = false,
  showEdit: _showEdit = false,
  showDelete = false,
  showSync: _showSync = false,
  enableReordering = true,
  generating = false,
  downloadingAll = false,
  syncing: _syncing = false,
  lastSynced: _lastSynced = null,
  onGenerate,
  onDownloadAll,
  onUpload,
  onDownload: _onDownload,
  onCardClick,
  onEdit,
  onDelete,
  onViewVersions: _onViewVersions,
  onSync: _onSync,
  onDocumentsReordered,
  onRename,
  onApprove,
  highlightDocumentId,
  title = "Case Files",
  emptyMessage = "No documents available.",
  generatingMessage = "Your case files are being generated and will appear here when ready.",
  searchValue = '',
  onSearchChange,
}: CaseFilesProps) {
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; doc: DocumentFile } | null>(null);
  const [localDocuments, setLocalDocuments] = useState<DocumentFile[]>(documents);
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; doc: DocumentFile | null }>({
    open: false,
    doc: null,
  });
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  // Approval request dialog state
  const [approvalDialog, setApprovalDialog] = useState<{ open: boolean; doc: DocumentFile | null }>({
    open: false,
    doc: null,
  });
  const [approvalAssignee, setApprovalAssignee] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [requestingApproval, setRequestingApproval] = useState(false);

  // Approve document confirmation dialog state
  const [approveDialog, setApproveDialog] = useState<{ open: boolean; doc: DocumentFile | null }>({
    open: false,
    doc: null,
  });
  const [approving, setApproving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update local documents when prop changes
  useEffect(() => {
    setLocalDocuments(documents);
  }, [documents, highlightDocumentId]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, doc: DocumentFile) => {
    setMenuAnchor({ element: event.currentTarget, doc });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleMenuAction = async (action: 'open' | 'edit' | 'rename' | 'delete' | 'request_approval' | 'approve') => {
    if (!menuAnchor) return;

    const doc = menuAnchor.doc;
    handleMenuClose();

    switch (action) {
      case 'open':
        if (onCardClick) onCardClick(doc);
        break;
      case 'edit':
        if (onEdit) onEdit(doc);
        break;
      case 'rename':
        setRenameValue(doc.name);
        setRenameError(null);
        setRenameDialog({ open: true, doc });
        break;
      case 'delete':
        if (onDelete) onDelete(doc);
        break;
      case 'request_approval':
        await handleRequestApproval(doc);
        break;
      case 'approve':
        await handleApproveDocument(doc);
        break;
    }
  };

  const handleRequestApproval = async (doc: DocumentFile) => {
    // Fetch available users for assignment
    try {
      const response = await fetch('/api/users/', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` }
      });
      const data = await response.json();
      setAvailableUsers(data.results || data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }

    setApprovalDialog({ open: true, doc });
  };

  const handleSubmitApprovalRequest = async () => {
    if (!approvalDialog.doc || !approvalAssignee || !caseId) return;

    setRequestingApproval(true);
    try {
      const response = await fetch(`/api/cases/${caseId}/documents/${approvalDialog.doc.id}/request_approval/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access')}`
        },
        body: JSON.stringify({
          assignee_id: approvalAssignee,
          notes: approvalNotes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to request approval');
      }

      // Close dialog and reset state
      setApprovalDialog({ open: false, doc: null });
      setApprovalAssignee('');
      setApprovalNotes('');
    } catch (error) {
      console.error('Failed to request approval:', error);
      alert('Failed to create approval request. Please try again.');
    } finally {
      setRequestingApproval(false);
    }
  };

  const handleApproveDocument = async (doc: DocumentFile) => {
    setApproveDialog({ open: true, doc });
  };

  const handleConfirmApproval = async () => {
    if (!approveDialog.doc) return;

    setApproving(true);
    try {
      if (onApprove) {
        // Use callback if provided
        await onApprove(approveDialog.doc);
      } else if (caseId) {
        // Fallback to direct API call
        const response = await fetch(`/api/cases/${caseId}/documents/set_operative/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access')}`
          },
          body: JSON.stringify({
            file_id: approveDialog.doc.id
          })
        });

        if (!response.ok) {
          throw new Error('Failed to approve document');
        }
      }

      // Close dialog - no reload needed
      setApproveDialog({ open: false, doc: null });
    } catch (error) {
      console.error('Failed to approve document:', error);
      alert('Failed to approve document. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleRenameSubmit = async () => {
    if (!renameDialog.doc || !onRename || !renameValue.trim()) return;

    setRenaming(true);
    setRenameError(null);
    try {
      // Pass null/empty for metadata fields since simple rename doesn't edit those
      await onRename(renameDialog.doc.id, renameValue, null, null, '', '');
      setRenameDialog({ open: false, doc: null });
      setRenameValue('');
    } catch (error: any) {
      console.error('Failed to rename document:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to rename document. Please try again.';
      setRenameError(errorMessage);
    } finally {
      setRenaming(false);
    }
  };

  const handleRenameCancel = () => {
    setRenameDialog({ open: false, doc: null });
    setRenameValue('');
    setRenameError(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalDocuments((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);

        // Update display_order on each document to match new position
        const newOrderWithUpdatedDisplayOrder = newOrder.map((doc, index) => ({
          ...doc,
          display_order: index
        }));

        // Defer the callback to avoid setState during render
        setTimeout(() => {
          if (onDocumentsReordered) {
            onDocumentsReordered(newOrderWithUpdatedDisplayOrder);
          }
        }, 0);

        return newOrderWithUpdatedDisplayOrder;
      });
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Group documents by lineage (root_document_id) and show operative or current version
  const groupedDocuments = localDocuments.reduce((acc, doc) => {
    const lineageKey = doc.root_document_id || doc.id;

    // Prioritize operative version, otherwise use current version
    if (!acc[lineageKey]) {
      // First document in this lineage
      acc[lineageKey] = doc;
    } else if (doc.is_operative) {
      // Always prefer operative version
      acc[lineageKey] = doc;
    } else if (!acc[lineageKey].is_operative && doc.is_current) {
      // If no operative yet, use the current version
      acc[lineageKey] = doc;
    }

    return acc;
  }, {} as Record<string, DocumentFile>);

  // Convert back to array and sort by display_order
  const sortedDocuments = Object.values(groupedDocuments).sort((a, b) => {
    const orderA = a.display_order ?? 0;
    const orderB = b.display_order ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  });

  // Apply search filter to the grouped/sorted documents
  const filteredDocuments = searchValue?.trim()
    ? sortedDocuments.filter((doc) => {
        const lowerSearch = searchValue.toLowerCase();
        return (
          doc.name?.toLowerCase().includes(lowerSearch) ||
          doc.filename?.toLowerCase().includes(lowerSearch)
        );
      })
    : sortedDocuments;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            📁 {title}
          </Typography>

          {/* Search and Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {onSearchChange && (
              <TextField
                size="small"
                placeholder="Search files..."
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250, backgroundColor: 'white' }}
              />
            )}

            {showGenerate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={generating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                onClick={onGenerate}
                disabled={generating || downloadingAll}
                size="medium"
              >
                {generating ? 'Generating...' : 'Generate All Documents'}
              </Button>
            )}

            {showDownloadAll && (
              <Button
                variant="outlined"
                startIcon={downloadingAll ? <CircularProgress size={16} /> : <ArchiveIcon />}
                onClick={onDownloadAll}
                disabled={documents.length === 0 || downloadingAll || generating}
              >
                {downloadingAll ? 'Preparing...' : 'Download All'}
              </Button>
            )}

            {showUpload && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onUpload}
                disabled={generating || downloadingAll}
              >
                Upload File
              </Button>
            )}
          </Box>
        </Box>

        {/* Content */}
        {generating ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
            sx={{ textAlign: 'center' }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Generating documents...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {generatingMessage}
            </Typography>
          </Box>
        ) : filteredDocuments.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredDocuments.map(doc => doc.id)}
              strategy={verticalListSortingStrategy}
              disabled={!enableReordering}
            >
              <List dense>
                {filteredDocuments.map((doc) => (
                  <SortableDocumentItem
                    key={doc.id}
                    doc={doc}
                    onMenuOpen={handleMenuOpen}
                    onCardClick={onCardClick || (() => {})}
                    isHighlighted={highlightDocumentId === doc.id}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        ) : (
          <Alert severity="info">
            {emptyMessage}
          </Alert>
        )}

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor?.element}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleMenuAction('open')}>
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleMenuAction('edit')}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>

          {menuAnchor && !menuAnchor.doc.is_operative && (
            <MenuItem onClick={() => handleMenuAction('request_approval')}>
              <ListItemIcon>
                <TaskIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Request Approval</ListItemText>
            </MenuItem>
          )}

          {menuAnchor && !menuAnchor.doc.is_operative && (
            <MenuItem onClick={() => handleMenuAction('approve')}>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Approve Document</ListItemText>
            </MenuItem>
          )}

          {showDelete && onDelete && (
            <MenuItem onClick={() => handleMenuAction('delete')}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Rename Dialog */}
        <Dialog
          open={renameDialog.open}
          onClose={handleRenameCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Rename Document</DialogTitle>
          <DialogContent>
            {renameError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {renameError}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Document Name"
              type="text"
              fullWidth
              value={renameValue}
              onChange={(e) => {
                setRenameValue(e.target.value);
                setRenameError(null); // Clear error when user starts typing
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRenameSubmit();
                }
              }}
              disabled={renaming}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRenameCancel} disabled={renaming}>
              Cancel
            </Button>
            <Button
              onClick={handleRenameSubmit}
              variant="contained"
              disabled={renaming || !renameValue.trim()}
            >
              {renaming ? <CircularProgress size={20} /> : 'Rename'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approval Request Dialog */}
        <Dialog
          open={approvalDialog.open}
          onClose={() => setApprovalDialog({ open: false, doc: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Request Document Approval</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Create a task to request approval for "{approvalDialog.doc?.name}"
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 1 }}>Assign To</Typography>
                <Select
                  value={approvalAssignee}
                  onChange={(e) => setApprovalAssignee(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value=""><em>Select user...</em></MenuItem>
                  {availableUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name && user.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Additional Notes (Optional)"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setApprovalDialog({ open: false, doc: null });
              setApprovalAssignee('');
              setApprovalNotes('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitApprovalRequest}
              variant="contained"
              disabled={!approvalAssignee || requestingApproval}
            >
              {requestingApproval ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approve Document Confirmation Dialog */}
        <Dialog
          open={approveDialog.open}
          onClose={() => setApproveDialog({ open: false, doc: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Approve Document</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ pt: 2 }}>
              Are you sure you want to approve "{approveDialog.doc?.name}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will mark it as the operative document and any other operative version will be unmarked.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setApproveDialog({ open: false, doc: null })}
              disabled={approving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmApproval}
              variant="contained"
              color="success"
              disabled={approving}
              startIcon={approving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {approving ? 'Approving...' : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
